import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom, Observable, Subscription, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ROUTE_PATHS } from '../../../../../shared/constants/route-paths';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { EditComponentType } from '../../../../../shared/enums/edit-component-type';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { OnUnsavedChanges } from '../../../../../shared/internal/on-unsaved-changes';
import { prefixWithSlash } from '../../../../../shared/internal/prefix-with-slash';
import { SidebarConfiguration } from '../../../../../shared/internal/sidebar/sidebar-configuration';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { DtcQuery } from '../../../../../shared/models/dtc/dtc-query';
import { Policy } from '../../../../../shared/models/info/policy';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { ItemBadge } from '../../../../../shared/models/internal/details/item-badge';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { PreselectionService } from '../../../../services/preselection.service';
import { TranslationService } from '../../../../services/translation.service';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { SidebarComponent } from '../../../common/sidebar/sidebar.component';
import { AddComponent } from '../../../icons/add/add.component';
import { DtcTableComponent } from '../../../tables/dtc/dtc-table/dtc-table.component';
import { CurrentLanguageAndSwPartNumberView } from '../../../sw-part-number/current-language-and-sw-part-number-view/current-language-and-sw-part-number-view';
import { Pagination } from '../../../../../shared/models/internal/pagination';
import { errorToString } from '../../../../common/common-utils';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';

@Component({
  selector: 'app-dtc-overview',
  imports: [
    DtcTableComponent,
    CommonModule,
    AddComponent,
    SidebarComponent,
    EnabledByPolicyDirective,
    CurrentLanguageAndSwPartNumberView,
    AgentActionDirective,
  ],
  templateUrl: './dtc-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-overview.component.scss',
})
export class DtcOverviewComponent
  extends CrudOverviewBaseComponent<Dtc>
  implements OnInit, OnUnsavedChanges<Dtc>, OnDestroy
{
  public dtcDetailConfiguration?: DetailsViewConfiguration;
  public dtcEditComponent = EditComponentType.Dtc;
  public dtcEditComponentId?: string;
  public sidebarConfiguration: SidebarConfiguration = new SidebarConfiguration();
  public recyclingModeEnabled = false;
  private languageServiceSubscription?: Subscription;
  private currentQuery?: DtcQuery;

  private dtcApi = inject(DtcApiService);
  private router = inject(Router);
  private preselectionService = inject(PreselectionService);
  private readonly translationService = inject(TranslationService);
  private accessRightService = inject(AccessRightsService);

  constructor() {
    super(STORAGE_KEYS.UNSAVED_DTC_CHANGES);
    this.sidebarConfiguration.translationsAreaEnabled = false;
    this.sidebarConfiguration.detailedTranslationsAreaEnabled = true;
    this.languageServiceSubscription = this.translationService.languageChangeSubject.subscribe({
      next: async () => {
        if (this.currentQuery) {
          if (this.selectedTableElement.isElementSelected) {
            const requestedPage = this.selectedTableElement.currentPage || 0;
            await this.fetchDtcsUntilPage(this.currentQuery, requestedPage);
            this.handleNewSelection(this.selectedTableElement.selectedElementId!);
            // TODO: We should consider to scroll to the element in the future
          } else {
            await this.fetchDtcsUntilPage(this.currentQuery, 0);
          }
        }
      },
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.configureReviewsEnabled(undefined);
    this.configureMergeReviewsEnabled();
    const dtcId = this.preselectionService.getIdAndClear(PreselectionType.Dtc);
    if (dtcId) {
      this.fetchDtc(dtcId);
    }
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.languageServiceSubscription) {
      this.languageServiceSubscription.unsubscribe();
    }
  }

  public onSearchTriggered(query: DtcQuery): void {
    if (this.areThereUnsavedChanges(this.getFriendlyItemName(this.selectedItem), () => this.search(query)) === false) {
      this.search(query);
    }
  }

  public onSortByChanged(query: DtcQuery): void {
    this.onSearchTriggered(query);
  }

  public createDtc(): void {
    this.router.navigate([prefixWithSlash(ROUTE_PATHS.dtcNew)]);
  }

  public onSoftDeleteTriggered(dtc: Dtc): void {
    this.dtcApi.setRemoveFlag(dtc?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(dtc?.id);
        this.sidebarOpen = false;
        this.toastr.success('DTC deactivated successfully');
      },
      error: (error) => {
        console.error('Error deactivating DTC:', errorToString(error));
      },
    });
  }

  public onHardDeleteTriggered(dtc: Dtc): void {
    this.dtcApi.deleteDtc(dtc?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(dtc?.id);
        this.sidebarOpen = false;
        this.toastr.success('DTC deleted successfully');
      },
      error: (error) => {
        console.error('Error deleting DTC:', errorToString(error));
      },
    });
  }

  public onRestoreTriggered(dtc: Dtc): void {
    this.dtcApi.restoreDtc(dtc?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(dtc?.id);
        this.sidebarOpen = false;
        this.toastr.success('DTC restored successfully');
      },
      error: (error) => {
        console.error('Error restoring DTC:', errorToString(error));
      },
    });
  }

  public async onDownloadTriggered(): Promise<void> {
    try {
      const blob = await firstValueFrom(this.dtcApi.downloadDtcCsvFile());
      if (blob.size == 0) {
        // do not download empty logs
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const swPartNumber = this.swPartNumberService.getSwPartNumber()?.number;
      a.download = `${swPartNumber || 'N/A'}-dtcs-${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke the object URL to free up memory
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading DTCs:', errorToString(error));
    }
  }

  public onScrolledToTheBottom(query: DtcQuery): void {
    this.fetchDtcs(query, false);
  }

  public onUpdatedDtc(dtc: Dtc): void {
    this.refreshItemInListAfterUpdate(dtc.id);
    this.selectedItem = dtc;
    if (dtc.id) {
      this.selectedTableElement.setSelectedElementId(dtc.id, this.paginationService.currentPageNumber);
    }
    this.clearUnsavedData();
    this.handleNewSelection(dtc.id ?? '');
  }

  protected override handleNewSelection(id: string): void {
    const item = this.getItemById(id);
    this.configureReviewsEnabled(item);
    this.configureMergeReviewsEnabled();
    this.sidebarOpen = true;
    this.sideBarTitle = `DTC ${this.getFriendlyItemName(item)}`;

    this.dtcApi.getDtc(id).subscribe({
      next: (dtc) => {
        this.configureDetailsView(dtc);
        this.dtcEditComponentId = dtc.id;
        this.selectedItem = dtc;
      },
      error: (error) => {
        console.error('Error fetching DTC:', errorToString(error));
      },
    });
  }

  protected override getFriendlyItemName(item?: Dtc): string {
    if (item) {
      return item.displayId ?? '';
    }
    return '';
  }

  protected override configureDetailsView(item: Dtc): void {
    const mainLanguageDtc = this.items!.filter((i) => i.id == item.id)[0];

    this.dtcDetailConfiguration = {
      title: item.displayId,
      shortDescription: item.swPartNumber?.displayId,
      translationStateTerminal: item.translationStateTerminal,
      translationStateDocumentation: item.translationStateDocumentation,
      singleLineDetails: [
        {
          sequence: 1,
          attributes: [
            { label: 'Priority', value: item.priority?.toString() ?? '' },
            { label: 'Group', value: item.group?.toString() ?? '' },
          ],
        },
      ],
      textAreas: [
        {
          sequence: 2,
          label: 'Implementation Hint',
          value: item.implementationHint ?? '-',
          isTranslatable: false,
        },
        {
          sequence: 4,
          label: 'Details on Terminal',
          value: item.detailsOnTerminal ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 9,
          label: 'Cause (Intern)',
          value: item.causeIntern ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 10,
          label: 'Remedy (Intern)',
          value: item.remedyIntern ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 7,
          label: 'Cause (Dealer)',
          value: item.causeDealer ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 8,
          label: 'Remedy (Dealer)',
          value: item.remedyDealer ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 5,
          label: 'Cause (Farmer)',
          value: item.causeFarmer ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          sequence: 6,
          label: 'Remedy (Farmer)',
          value: item.remedyFarmer ?? '-',
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
      ],
      displayLinkedDtcs: false,
      badge: {
        itemBadgeLabel: 'Linked error message (Code - Header)',
        title: `${mainLanguageDtc.errorMessage?.code ?? 'No error message assigned'} ${mainLanguageDtc.errorMessage?.code ? '-' : ''} ${mainLanguageDtc.errorMessage?.headerOnTerminal ?? ''}`,
        tooltip: mainLanguageDtc.errorMessage?.detailsOnTerminal ?? '',
        isEmpty: !mainLanguageDtc.errorMessage,
        itemType: mainLanguageDtc.errorMessage?.id ? PreselectionType.ErrorMessage : PreselectionType.Unknown,
        itemId: mainLanguageDtc.errorMessage?.id,
        itemSearchTerm: mainLanguageDtc.errorMessage?.headerOnTerminal,
        sequence: 3,
      } as ItemBadge,
      internalNotes: item.internalNotes,
    };
  }

  protected configureReviewsEnabled(item: Dtc | undefined): void {
    this.accessRightService.getAccessRights$().subscribe((ar) => {
      this.sidebarConfiguration.reviewAreaEnabled =
        item?.swPartNumber?.state != SwPartNumberState.Snapshot &&
        this.translationService.defaultLanguageSelected() &&
        (ar.authorized(Policy.canCreateTechnicalOrganisationDtcReviewRequests) ||
          ar.authorized(Policy.canCreateAfterSalesDtcReviewRequests));
    });
  }

  protected configureMergeReviewsEnabled(): void {
    this.accessRightService.getAccessRights$().subscribe((ar) => {
      this.sidebarConfiguration.mergeReviewAreaEnabled =
        this.translationService.defaultLanguageSelected() && ar.authorized(Policy.canMergeDtcReviewRequests);
    });
  }

  private async fetchDtcs(query: DtcQuery, resetPagination: boolean): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedData(resetPagination, (page, pageSize) => this.getRecycledOrNormalDtcs(query, page, pageSize));
  }

  private async fetchDtcsUntilPage(query: DtcQuery, targetPage: number): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedDataUntilPage(targetPage, (page, pageSize) =>
      this.getRecycledOrNormalDtcs(query, page, pageSize)
    );
  }

  private getRecycledOrNormalDtcs(query: DtcQuery, page: number, pageSize: number): Observable<Pagination<Dtc>> {
    query.page = page;
    query.pageSize = pageSize;
    if (this.recyclingModeEnabled) {
      return this.dtcApi.getRecycledDtcs(
        query,
        TranslationService.defaultLanguageCode(),
        this.translationService.getLanguage()
      );
    }
    return this.dtcApi.getDtcs(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage());
  }

  private fetchDtc(id: string): void {
    const query = {
      ids: id,
    } as DtcQuery;

    this.fetchPagedData(true, (page, pageSize) => {
      query.page = page;
      query.pageSize = pageSize;
      return this.dtcApi
        .getDtcs(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage())
        .pipe(
          tap((dtcs) => {
            const dtc = dtcs.items.filter((dtc) => dtc.id == id)[0];
            if (!dtc) {
              console.error(`Couldn't find preselected DTC with id ${id}`);
              return;
            }
            this.swPartNumberService.setSwPartNumber(dtc.swPartNumber!);
            this.onItemClicked(id);
          })
        );
    });
  }

  private refreshItemInListAfterUpdate(id?: string): void {
    if (!id) {
      return;
    }
    const query = {
      ids: id,
    } as DtcQuery;
    this.dtcApi
      .getDtcs(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage())
      .subscribe({
        next: (dtcs) => {
          const dtc = dtcs.items.shift();
          if (dtc) {
            this.items = this.items?.map((existingDtc) => (existingDtc.id === dtc.id ? dtc : existingDtc));
          }
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
  }

  private search(query: DtcQuery): void {
    this.items = [];
    this.sidebarOpen = false;
    this.selectedItem = undefined;
    this.selectedTableElement.clearSelection();
    this.fetchDtcs(query, true);
  }
}
